/**
 * Single source for reading auth state from localStorage (survives page refresh).
 */

/** Dispatched on same-tab login/logout when token or user is persisted. */
export const AUTH_SESSION_CHANGED_EVENT = 'leap-auth-session-changed';

export const GUEST_ONLY_ROUTE_PREFIXES = [
  '/login',
  '/reset-password',
  '/lost-invite',
  '/register',
  '/invite',
];

/** Auth pages — used to reject post-login return URLs */
export const AUTH_ROUTE_PREFIXES = [...GUEST_ONLY_ROUTE_PREFIXES, '/setup-2fa', '/update-auth'];

export function notifyAuthSessionChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_SESSION_CHANGED_EVENT));
  }
}

export function clearAuthSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  notifyAuthSessionChanged();
}

export function persistAuthToken(token) {
  localStorage.setItem('token', token);
  notifyAuthSessionChanged();
}

export function persistAuthUser(user) {
  localStorage.setItem('user', user);
  notifyAuthSessionChanged();
}

/** Persists a parsed user record (object) and notifies subscribers. */
export function persistAuthUserRecord(userRecord) {
  if (!userRecord) {
    localStorage.removeItem('user');
    notifyAuthSessionChanged();
    return;
  }
  persistAuthUser(JSON.stringify(userRecord));
}

export function readStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Login OTP step when backend requires 2FA for this IP (require2FA + mfaMethod). */
export function needsLoginOtp(user) {
  return Boolean(user?.require2FA && user?.mfaMethod);
}

/** Forced MFA enrollment before dashboard (require2FA without mfaMethod). */
export function needsSetup2FA(user) {
  return Boolean(user?.require2FA && !user?.mfaMethod);
}

export function needsPasswordUpdate(user) {
  return Boolean(user?.requirePasswordUpdate);
}

/** Matches prod Dashboard guard — any incomplete 2FA flag blocks the app shell. */
export function blocksDashboardAccess(user) {
  return Boolean(user?.require2FA || user?.requirePasswordUpdate);
}

export function readAuthSession() {
  const token = localStorage.getItem('token');
  const user = readStoredUser();
  const isAuthenticated = Boolean(token && user);

  const needsSetup2FAFlag = needsSetup2FA(user);
  const needsPasswordUpdateFlag = needsPasswordUpdate(user);
  const isPendingLogin2FA = needsLoginOtp(user);

  const isSessionComplete = Boolean(
    isAuthenticated && !needsSetup2FAFlag && !needsPasswordUpdateFlag && !isPendingLogin2FA,
  );

  return {
    token,
    user,
    isAuthenticated,
    needsSetup2FA: needsSetup2FAFlag,
    needsPasswordUpdate: needsPasswordUpdateFlag,
    isPendingLogin2FA,
    isSessionComplete,
  };
}

export function matchRoutePrefix(pathname, prefixes) {
  const lowerPath = (pathname || '').toLowerCase();
  for (const prefix of prefixes) {
    if (lowerPath === prefix || lowerPath.startsWith(`${prefix}/`)) {
      return true;
    }
  }
  return false;
}

export function isGuestOnlyRoute(pathname) {
  return matchRoutePrefix(pathname, GUEST_ONLY_ROUTE_PREFIXES);
}

export function isAuthRoute(pathname) {
  return matchRoutePrefix(pathname, AUTH_ROUTE_PREFIXES);
}
